// 用途: メンテナンス状態（503応答時の情報）をフロント全体で共有するサービス
// - Retry-After（秒 or HTTP日付）を解釈して残り時間を算出
// - 画面（maintenance.page）やインターセプタから利用
import { Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

export interface MaintenanceState {
  active: boolean;
  message?: string | null;
  retryAfterSeconds?: number | null;
  retryAt?: Date | null;
  lastRequestId?: string | null;
}

@Injectable({providedIn: 'root'})
export class MaintenanceStateService {
  // Signal で状態管理（Angular 20）
  private readonly _state = signal<MaintenanceState>({
    active: false,
    message: null,
    retryAfterSeconds: null,
    retryAt: null,
    lastRequestId: null,
  });

  get state() {
    return this._state.asReadonly();
  }

  set(state: Partial<MaintenanceState>) {
    this._state.update((prev) => ({
      ...prev,
      ...state,
    }));
  }

  clear() {
    this._state.set({
      active: false,
      message: null,
      retryAfterSeconds: null,
      retryAt: null,
      lastRequestId: null,
    });
  }

  setFromHttpError(err: HttpErrorResponse) {
    const body = (err.error && typeof err.error === 'object') ? err.error as { message?: unknown; requestId?: unknown } : {};
    const message =
      typeof body?.message === 'string'
        ? body.message
        : (err.message || 'Service Unavailable');

    // Retry-After は秒 or HTTP日付
    const retryHeader = err.headers?.get?.('Retry-After') ?? null;
    const {seconds, date} = this.parseRetryAfter(retryHeader);

    this._state.set({
      active: true,
      message,
      retryAfterSeconds: seconds,
      retryAt: date,
      lastRequestId: typeof body?.requestId === 'string' ? body.requestId : null,
    });
  }

  private parseRetryAfter(retryAfter: string | null): { seconds: number | null; date: Date | null } {
    if (!retryAfter) return {seconds: null, date: null};
    const sec = Number(retryAfter);
    if (Number.isFinite(sec)) {
      const date = new Date(Date.now() + sec * 1000);
      return {seconds: Math.max(0, Math.floor(sec)), date};
    }
    // HTTP日付（RFC1123）
    const d = new Date(retryAfter);
    if (!Number.isNaN(d.getTime())) {
      const diffMs = d.getTime() - Date.now();
      return {
        seconds: diffMs > 0 ? Math.floor(diffMs / 1000) : 0,
        date: d,
      };
    }
    return {seconds: null, date: null};
  }
}
